import { useState, useCallback, useRef, useEffect } from 'react';
import { apiFetch, track } from '../dppApi';
import { useLocalLang, LANG_LABELS } from '../hooks/useLocalLang';

const SUGGESTIONS = [
    'What is my revenue this quarter?',
    'Where are my viewers located?',
    'Who are my most valuable customers?',
];

const SpeechRecognition =
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const speechSupported = !!SpeechRecognition;

// Web Speech can't auto-detect the spoken language, so we dictate in the
// browser's language; the backend then detects/translates the transcript to
// English (same path as typed text) before sending it to Claude.
const DICTATION_LOCALE =
    (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';

const MicIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
);

// Ask Bloom: the brand's free-form, brand-scoped, read-only data assistant.
// Asks the brand_query_agent a natural-language question (any language) and
// shows the plain answer, with the underlying rows on demand.
const BrandAssistant = ({ token }) => {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showData, setShowData] = useState(false);
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);
    const inputRef = useRef(null);

    // EN/local toggle — same location-based detection Passport.js uses. The
    // toggle (once shown) always pins the reply language; with no local
    // language detected, no toggle appears and the assistant keeps matching
    // the question's own language (the pre-existing behavior).
    const localLang = useLocalLang();
    const [showLocal, setShowLocal] = useState(false);
    const localLabel = LANG_LABELS[localLang] || localLang?.toUpperCase();
    const replyLang = localLang ? (showLocal ? localLang : 'en') : null;

    // Translate the suggestion chips into the local language (same batch-translate
    // endpoint/pattern Passport.js uses for its static field values) — cached per
    // language so switching back and forth doesn't re-request it.
    const [chipsT, setChipsT] = useState({}); // { [lang]: SUGGESTIONS translated, same order }
    useEffect(() => {
        if (!showLocal || !localLang || chipsT[localLang]) return;
        apiFetch('/api/passport/translate-batch', { method: 'POST', body: { lang: localLang, texts: SUGGESTIONS } })
            .then(d => {
                if (Array.isArray(d.translations) && d.translations.length === SUGGESTIONS.length) {
                    setChipsT(prev => ({ ...prev, [localLang]: d.translations }));
                }
            })
            .catch(() => {});
    }, [showLocal, localLang, chipsT]);
    const displaySuggestions = (showLocal && chipsT[localLang]) || SUGGESTIONS;

    // Grow the textarea to fit its content so the full question stays visible.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [question]);

    const submit = useCallback(async (text) => {
        const q = (text ?? question).trim();
        if (!q || loading) return;
        track('brand', 'ask_bloom_send');
        setLoading(true);
        setError(null);
        setResult(null);
        setShowData(false);
        try {
            const body = { question: q };
            if (replyLang) body.lang = replyLang;
            const data = await apiFetch('/api/brand/assistant/ask', {
                method: 'POST', token, body,
            });
            setResult(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [question, loading, token, replyLang]);

    const toggleMic = useCallback(() => {
        if (!speechSupported) return;
        if (listening) { recognitionRef.current?.stop(); return; }
        const rec = new SpeechRecognition();
        rec.lang = DICTATION_LOCALE;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setQuestion(transcript);
            submit(transcript);
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);
        recognitionRef.current = rec;
        setListening(true);
        rec.start();
    }, [listening, submit]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
    };

    const reset = useCallback(() => {
        recognitionRef.current?.stop();
        setQuestion('');
        setResult(null);
        setError(null);
        setShowData(false);
        inputRef.current?.focus();
    }, []);

    return (
        <div className="dpp-card insights-card assistant-card">
            <div className="assistant-card-header">
                <div className="insights-card-title">Ask Bloom</div>
                {localLang && (
                    <div className="assistant-lang-toggle">
                        <button
                            className={`lang-btn${!showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(false)}
                        >EN</button>
                        <button
                            className={`lang-btn${showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(true)}
                        >{localLabel}</button>
                    </div>
                )}
            </div>

            <div className="assistant-input-row">
                <button
                    type="button"
                    className={`assistant-mic${listening ? ' is-listening' : ''}`}
                    onClick={toggleMic}
                    disabled={!speechSupported}
                    title={speechSupported ? (listening ? 'Stop' : 'Speak your question') : 'Voice input not supported'}
                    aria-label={listening ? 'Stop listening' : 'Speak your question'}
                >
                    {MicIcon}
                </button>
                <textarea
                    ref={inputRef}
                    className="dpp-input assistant-input"
                    rows={1}
                    placeholder=""
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <button className="dpp-button" onClick={() => submit()} disabled={loading || !question.trim()}>
                    {loading ? '…' : 'Ask'}
                </button>
            </div>

            {listening && (
                <div className="assistant-listening-status" role="status" aria-live="polite">
                    <span className="assistant-listening-dot" aria-hidden="true" />
                    Listening…
                </div>
            )}

            {!result && !loading && (
                <div className="assistant-suggestions">
                    {displaySuggestions.map((label, i) => (
                        <button key={SUGGESTIONS[i]} className="assistant-chip" onClick={() => { setQuestion(label); submit(label); }}>
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {loading && <p className="assistant-status">Thinking…</p>}
            {error && <p className="dpp-error">{error}</p>}

            {result && (
                <div className="assistant-result">
                    <p className="assistant-answer">{result.answer}</p>
                    {result.columns?.length > 0 && (
                        <>
                            <button className="assistant-toggle" onClick={() => setShowData(v => !v)}>
                                {showData ? 'Hide data' : 'Show data'}
                            </button>
                            {showData && (
                                <div className="assistant-details">
                                    <div className="assistant-table-wrap">
                                        <table className="assistant-table">
                                            <thead>
                                                <tr>{result.columns.map(c => <th key={c}>{c}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {result.rows.slice(0, 50).map((row, i) => (
                                                    <tr key={i}>
                                                        {row.map((cell, j) => (
                                                            <td key={j}>{cell === null ? '—' : String(cell)}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {result.truncated && <p className="assistant-status">Showing first {result.rows.length} rows.</p>}
                                </div>
                            )}
                        </>
                    )}
                    <div className="assistant-clear-row">
                        <button type="button" className="assistant-clear" onClick={reset}>
                            Clear
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandAssistant;
